import { Dialog } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TypeAttachment } from '@app/core/models';
import { SystematicService } from '@app/core/services/systematic.service';
import { ModalSystematicReadComponent } from '@app/modules/eclass/components/modal-systematic-read/modal-systematic-read.component';
import { SHARED_MODULES } from '@app/shared';
import { notNull, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isArray, omit } from 'lodash';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'app-shortcut-systematic-list',
  imports: [CommonModule, SHARED_MODULES],
  templateUrl: './shortcut-systematic-list.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortcutSystematicListComponent implements OnInit {
  private readonly _store = inject(Store);
  private readonly _dialog = inject(Dialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _systematicService = inject(SystematicService);
  private readonly _translateService = inject(TranslateService);

  protected systematicsCount = signal<number>(0);
  protected systematics$: Observable<any>;
  protected loading = signal<boolean>(true);
  protected lastSystematicTitle = signal<string>('');

  public ngOnInit(): void {
    this.loadSystematics();
  }

  protected onOpenEducationalProjectModal(systematic: any): void {
    const activeUser$ = this._store.select(AppSelectors.ActiveUser);

    activeUser$
      .pipe(
        notNull(),
        take(1),
        switchMap(() => this._dialog.open(ModalSystematicReadComponent, { data: { systematic } }).closed),
      )
      .subscribe();
  }

  private loadSystematics() {
    this.systematics$ = this._store.select(AppSelectors.appFeature).pipe(
      filter((store) => !!store.usuario_ativo),
      takeUntilDestroyed(this._destroyRef),
      tap(() => this.loading.set(true)),
      switchMap((data) => {
        const obj = {
          employee_id: data.funcionario_id,
          student_id: data.usuario_ativo?.aluno_id,
          type: 1,
        };
        return this._systematicService
          .index(omit(obj, data.usuario_ativo?.aluno_id ? ['employee_id'] : ['student_id']))
          .pipe(
            safeEmpty(),
            map((data) => (isArray(data) ? data : [])),
          );
      }),
      tap(() => this.loading.set(false)),
      map((data: any) => {
        if (data.length > 0) {
          const latestSystematic = data.reduce((latest, current) =>
            new Date(current.created_at) > new Date(latest.created_at) ? current : latest,
          );
          this.lastSystematicTitle.set(latestSystematic.title);
        }

        return data
          .map((item) =>
            !!item.description
              ? {
                  ...item,
                  attachments: [
                    {
                      id: undefined,
                      file: undefined,
                      title: this._translateService.instant('geral.conteudo'),
                      size: undefined,
                      extension: 'html',
                      type: TypeAttachment.HTML,
                      html: item.description,
                    },
                    ...item.attachments,
                  ],
                }
              : item,
          )
          .slice(0, 3);
      }),
    );
  }
}
